/* eslint-disable camelcase */
import React, { useMemo } from 'react';
import {
  withStyles,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';
import {
  DispatchDragNDropTypes, DispatchOperationType, DispatchStatusNumber, DispatchStatus,
} from '~/utils';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import PF2MEquipIcon from '~/components/PF2MEquipIcon';
import PF2MDispatchIcon from '~/components/PF2MDispatchIcon';
import { setSelectedTruckOrigin, setSelectedLoadOrigin } from '~/store/dispatch/actions';
import { calculateDurationBytimestamp } from '~/utils/moment';
import { getCodeFromStatus, toCodesMapByKey } from '~/utils/codes';


const getWarningType = (truckOrigin, loadAllocations, isGenericRow) => {
  if (isGenericRow) return 'standardWarning';
  const expectedLoadAllocation = loadAllocations
    .find(r => r.id === truckOrigin.load_allocation_id);

  if (expectedLoadAllocation
    && truckOrigin.current_excavator_id !== expectedLoadAllocation.equip_id) {
    return 'standardWarning';
  }
  return null;
};

const OriginRow = ({
  onTruckDrop,
  origin,
  leftLane,
  rightLane,
  isGenericRow = false,
  loadAllocations,
  openTruckModal,
  openExcavatorModal,
  onTruckContextMenu,
  onLoadContextMenu,
}) => {
  const order = [
    DispatchOperationType.LOAD,
    DispatchOperationType.UNLOAD,
    DispatchOperationType.LOAD_MANEUVER,
    DispatchOperationType.UNLOAD_MANEUVER,
    DispatchOperationType.LOAD_QUEUE,
    DispatchOperationType.UNLOAD_QUEUE,
    DispatchOperationType.LOAD_TRAFFIC,
    DispatchOperationType.UNLOAD_TRAFFIC,
    DispatchOperationType.EMPTY_STOP,
    DispatchOperationType.RELEASED_EQUIPMENT,
  ];

  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const equipmentStatuses = useSelector(state => state.dispatch.equipmentStatuses);
  const codes = useSelector(state => state.manager.codes);
  const operators = useSelector(state => state.manager.operators);
  const materials = useSelector(state => state.nref.materials);
  const elements = useSelector(state => state.nref.elements);
  const subElements = useSelector(state => state.nref.subElements);
  const syncStatus = useSelector(state => state.dispatch.syncStatus);

  const statusByEquipId = useMemo(() => (
    Object.fromEntries(equipmentStatuses?.map(e => [e.id_equip, e]) || [])
  ), [equipmentStatuses]);

  const codesByKey = useMemo(() => toCodesMapByKey(codes), [codes]);

  const operatorById = useMemo(() => (
    Object.fromEntries(operators.map(o => [o.id_operator, o]))
  ), [operators]);

  const materialById = useMemo(() => (
    Object.fromEntries(materials.map(m => [m.id, m]))
  ), [materials]);

  const elementById = useMemo(() => (
    Object.fromEntries(elements.map(e => [e.id, e]))
  ), [elements]);

  const subElementById = useMemo(() => (
    Object.fromEntries(subElements.map(s => [s.id, s]))
  ), [subElements]);

  const syncStatusByEquipId = useMemo(() => (
    Object.fromEntries(syncStatus.map(s => [s.equip_id, s]))
  ), [syncStatus]);

  const leftLaneSorted = useMemo(() => leftLane.sort(
    (a, b) => order.indexOf(a.operation_type) - order.indexOf(b.operation_type),
  ), [leftLane, order]);

  const leftLaneDurations = leftLaneSorted.map((left) => {
    const matchingStatuses = statusByEquipId[left.equip_id];

    const durations = matchingStatuses
      ? calculateDurationBytimestamp(matchingStatuses.start_timestamp)
      : null;

    const code = getCodeFromStatus(codesByKey, matchingStatuses);
    const codeType = code?.code_type;

    const statusValue = codeType === undefined
      ? left.status
      : DispatchStatusNumber[codeType];

    return {
      ...left,
      durations,
      start_timestamp: matchingStatuses?.start_timestamp,
      codes_id: code?.id,
      status: statusValue,
      codes_name: code?.name ?? translate('validation:CodeNotFound'),
      operators_name:
        operatorById[matchingStatuses?.id_operator]?.name
        ?? translate('validation:OperatorNotFound'),
      current_material_name:
        materialById[left.current_material_id]?.name
        ?? translate('validation:MaterialNotFound'),
      current_element_name:
        elementById[matchingStatuses?.element_id]?.name
        ?? translate('validation:ElementNotFound'),
      current_origin_point_name:
        subElementById[matchingStatuses?.point_id]?.name
        ?? translate('validation:SubElementNotFound'),
      timestamp_sync_request:
        syncStatusByEquipId[matchingStatuses?.id_equip]?.timestamp_sync_request
        ?? null,
    };
  });

  const rightLaneSorted = useMemo(() => rightLane.sort(
    (a, b) => order.indexOf(a.operation_type) - order.indexOf(b.operation_type),
  ), [rightLane, order]);

  const rightLaneDurations = rightLaneSorted.map((right) => {
    const matchingStatuses = statusByEquipId[right.equip_id];
    const durations = matchingStatuses
      ? calculateDurationBytimestamp(matchingStatuses.start_timestamp)
      : null;

    const code = getCodeFromStatus(codesByKey, matchingStatuses);
    const codeType = code?.code_type;

    const statusValue = codeType === undefined
      ? right.status
      : DispatchStatusNumber[codeType];

    return {
      ...right,
      durations,
      start_timestamp: matchingStatuses?.start_timestamp,
      codes_id: code?.id,
      status: statusValue,
      codes_name: code?.name ?? translate('validation:CodeNotFound'),
      operators_name:
        operatorById[matchingStatuses?.id_operator]?.name
        ?? translate('validation:OperatorNotFound'),
      current_material_name:
        materialById[right.current_material_id]?.name
        ?? translate('validation:MaterialNotFound'),
      current_element_name:
        elementById[matchingStatuses?.element_id]?.name
        ?? translate('validation:ElementNotFound'),
      current_origin_point_name:
        subElementById[matchingStatuses?.point_id]?.name
        ?? translate('validation:SubElementNotFound'),
      timestamp_sync_request:
        syncStatusByEquipId[matchingStatuses?.id_equip]?.timestamp_sync_request
        ?? null,
    };
  });

  const [{ dragItem }, truckDrop] = useDrop({
    accept: origin.id === null ? []
      : [DispatchDragNDropTypes.ORIGIN_TRUCK, DispatchDragNDropTypes.POOL_TRUCK],

    drop: (monitor) => {
      if (origin.id === null) return;

      if (origin.id !== null
        && monitor.obj.current_excavator_id !== origin.equip_id) {
        onTruckDrop(monitor.obj, origin.id);
      }
    },

    collect: monitor => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
      dragItem: monitor.getItem(),
    }),
  });

  return (
    <div
      key={origin.id}
      style={{
        display: 'flex',
        width: '100%',
        height: 'fit-content',
        backgroundColor: '#dedede',
      }}
      ref={truckDrop}
    >
      <div
        style={{
          width: '50%',
          display: 'flex',
          flexDirection: 'row-reverse',
          alignItems: 'center',
          gap: '20px',
          border: '1px solid #CDCDCD',
          paddingBlock: 5,
        }}
      >
        <div>
          <PF2MDispatchIcon
            iconType="stack"
            customStyles={{
              marginRight: '10px',
              cursor: 'default',
            }}
          />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
          {
            leftLaneDurations.map(r => (
              <PF2MEquipIcon
                key={r.equip_id}
                extraInfo={r.current_destination_point_name}
                item={r}
                itemType={DispatchDragNDropTypes.ORIGIN_TRUCK}
                useOperationType={r.status === DispatchStatus.OP}
                customStyles={{
                  gap: 5,
                  opacity: (dragItem && dragItem.obj?.equip_id === r.equip_id) ? 0 : 1,
                }}
                warningType={getWarningType(r, loadAllocations, isGenericRow)}
                onClick={() => {
                  dispatch(setSelectedTruckOrigin(r));
                  openTruckModal(r);
                }}
                onContextMenu={onTruckContextMenu}
                tooltipData={r}
                v2
              />
            ))
          }
        </div>
      </div>
      <div
        style={{
          gap: '20px',
          width: '50%',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row-reverse',
          border: '1px solid #CDCDCD',
          paddingBlock: 5,
        }}
      >
        <div>
          <PF2MEquipIcon
            item={origin}
            itemType="load"
            customStyles={{
              marginRight: 15,
            }}
            onContextMenu={onLoadContextMenu}
            onClick={() => {
              if (!isGenericRow) {
                dispatch(setSelectedLoadOrigin(origin));
                openExcavatorModal(origin);
              }
            }}
            warningType={
              (!origin.priority && !isGenericRow ? 'standardWarning' : null)
            }
            tooltipData={origin}
            v2
          />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
          {
            rightLaneDurations.map(r => (
              <PF2MEquipIcon
                key={r.equip_id}
                extraInfo={r.load_allocation_excavator_name}
                item={r}
                itemType={DispatchDragNDropTypes.ORIGIN_TRUCK}
                useOperationType={r.status === DispatchStatus.OP}
                customStyles={{
                  gap: 5,
                  opacity: (dragItem && dragItem.obj?.equip_id === r.equip_id) ? 0 : 1,
                }}
                onContextMenu={onTruckContextMenu}
                warningType={getWarningType(r, loadAllocations, isGenericRow)}
                onClick={() => {
                  dispatch(setSelectedTruckOrigin(r));
                  openTruckModal(r);
                }}
                tooltipData={r}
                v2
              />
            ))
          }
        </div>
      </div>
    </div>
  );
};

OriginRow.propTypes = {
  onTruckContextMenu: PropTypes.func,
  onTruckDrop: PropTypes.func,
  origin: PropTypes.object,
  leftLane: PropTypes.array,
  rightLane: PropTypes.array,
  isGenericRow: PropTypes.bool,
  loadAllocations: PropTypes.array,
  openTruckModal: PropTypes.func,
  openExcavatorModal: PropTypes.func,
  onLoadContextMenu: PropTypes.func,
};

OriginRow.defaultProps = {
  onTruckDrop: () => null,
  origin: {},
  leftLane: [],
  rightLane: [],
  isGenericRow: false,
  loadAllocations: [],
  openTruckModal: () => null,
  openExcavatorModal: () => null,
  onTruckContextMenu: null,
  onLoadContextMenu: null,
};

export default withStyles(styles)(OriginRow);
